import { downloadExcel } from '../../utils/excelFileHelper';
import type {
    ExcelFilePrefix,
    ProcessedRow,
    AutoReturnReportDetailItem,
    AutoReturnReportItemStatus,
    AutoReturnReport,
    AutoReturnReportDetailsResponse,
    AutoReturnReportsResponse,
    AutoReturnReportStatus,
    AutoReturnAndResendTab,
    ProcessedRowStatus,
    ShipmentRow,
} from './autoReturnAndResend.types';

export const MAX_SHIPMENT_COUNT = 1000;

export const filterShippingInput = (value: string): string => value.replace(/[^0-9\n,;\s]/g, '');

export const parseShippingNumbers = (input: string): string[] => {
    const numbers = input
        .split(/[\n,;\s]+/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

    return [...new Set(numbers)];
};

export const resolveInitialActiveTab = (
    resendCount: number,
    autoReturnCount: number,
    notFoundCount: number,
    processedCount = 0
): AutoReturnAndResendTab => {
    if (resendCount > 0) {
        return 'resend';
    }

    if (autoReturnCount > 0) {
        return 'autoReturn';
    }

    if (notFoundCount > 0) {
        return 'notFound';
    }

    if (processedCount > 0) {
        return 'processed';
    }

    return 'resend';
};

export const formatProcessedDateTime = (date: Date | string): string => {
    const d = new Date(date);

    if (Number.isNaN(d.getTime())) {
        return '-';
    }

    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hour = String(d.getHours()).padStart(2, '0');
    const minute = String(d.getMinutes()).padStart(2, '0');

    return `${day}.${month}.${year} ${hour}:${minute}`;
};

export const buildExcelFileName = (prefix: ExcelFilePrefix): string => {
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();

    return `${prefix}-${dd}.${mm}.${yyyy}.xlsx`;
};

type ExcelColumnMapping<T> = Array<{
    key: string;
    label: string;
    resolve: (row: T) => string | number;
}>;

export const exportPanelToExcel = <T>(
    rows: T[],
    columnMapping: ExcelColumnMapping<T>,
    fileNamePrefix: ExcelFilePrefix
) => {
    if (rows.length === 0) {
        return;
    }

    const excelDownloadData = rows.map((row) => {
        const mappedRow: Record<string, string | number> = {};

        columnMapping.forEach(({ key, resolve }) => {
            mappedRow[key] = resolve(row);
        });

        return mappedRow;
    });

    downloadExcel({
        excelDownloadData,
        excelDownloadFolderName: fileNamePrefix,
        fileNameOverride: buildExcelFileName(fileNamePrefix),
    });
};

export const getCurrentOperatorUsername = (): string => {
    try {
        const operator = JSON.parse(localStorage.getItem('checkOperator') || '{}');
        return operator?.username ?? '-';
    } catch {
        return '-';
    }
};

export const buildProcessedRow = (
    row: ShipmentRow,
    processedBy: string,
    status: ProcessedRowStatus = 'queued'
): ProcessedRow => ({
    ...row,
    processedBy,
    processedAt: new Date().toISOString(),
    status,
});

export const resolveMockItemStatus = (shippingListNo: string): AutoReturnReportItemStatus => {
    const lastDigit = Number.parseInt(shippingListNo.slice(-1), 10);

    if (Number.isNaN(lastDigit)) {
        return 'failed';
    }

    return lastDigit >= 7 && lastDigit <= 9 ? 'failed' : 'success';
};

export const isSuccessfulReportItem = (item: AutoReturnReportDetailItem): boolean => {
    const status = `${item.statusKey ?? item.statusDescription ?? item.status ?? ''}`.toLowerCase();

    return item.status === 'success' || ['success', 'successful', 'completed', 'başarılı', 'tamamlandı'].includes(status);
};

export const resolveReportStatus = (items: AutoReturnReportDetailItem[] = []): AutoReturnReportStatus => {
    const hasFailed = items.some((item) => !isSuccessfulReportItem(item));
    return hasFailed ? 'failed' : 'completed';
};

export const getReportId = (report: AutoReturnReport): string =>
    String(report.rpaRequestId ?? report.reportId ?? '-');

export const getReportDate = (report: AutoReturnReport): string =>
    formatProcessedDateTime(report.createDate ?? report.createdAt ?? '');

export const getReportUserName = (report: AutoReturnReport): string =>
    report.userName ?? report.processedBy ?? '-';

export const getReportItems = (report: AutoReturnReport): AutoReturnReportDetailItem[] => report.items ?? [];

export const isReportSuccessful = (report: AutoReturnReport): boolean => {
    if (report.status) {
        return report.status === 'completed';
    }

    const status = `${report.statusKey ?? report.statusDescription ?? ''}`.toLowerCase();
    if (status) {
        return ['success', 'successful', 'completed', 'başarılı', 'tamamlandı'].includes(status);
    }

    return resolveReportStatus(getReportItems(report)) === 'completed';
};

export const normalizeAutoReturnReports = (response: AutoReturnReportsResponse): AutoReturnReport[] => {
    if (Array.isArray(response)) {
        return response;
    }

    return response?.items ?? response?.data ?? [];
};

export const normalizeAutoReturnReportDetails = (
    response: AutoReturnReportDetailsResponse,
    report: AutoReturnReport
): AutoReturnReport => {
    if (Array.isArray(response)) {
        return { ...report, items: response };
    }

    if (response?.items) {
        return { ...report, ...response, items: response.items };
    }

    if ('data' in response && Array.isArray(response.data)) {
        return { ...report, items: response.data };
    }

    if ('data' in response && response.data) {
        return { ...report, ...response.data };
    }

    return { ...report, ...response };
};
